import { inject, Injectable } from '@angular/core';
import { Auth, authState, createUserWithEmailAndPassword, fetchSignInMethodsForEmail, GoogleAuthProvider, idToken, sendEmailVerification, sendPasswordResetEmail, signInWithCustomToken, signInWithEmailAndPassword, signInWithPopup, signOut, UserCredential } from '@angular/fire/auth';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, filter, firstValueFrom, map, Subject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { DomSanitizer } from '@angular/platform-browser';
import { WhiteLabelConfig } from 'voxr-shared';
import { addHeaderTag, translateWithCasing } from '../utility';
import { SwUpdate } from '@angular/service-worker';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { interval } from 'rxjs';

export const SSO_REDIRECT_PARAM = 'sso-redirect';

export type MessagePayload = {
  severity?: 'info' | 'success' | 'danger' | 'warning';
  summary?: string;
  detail: string;
  duration?: number;
  sticky?: boolean;
};

export type ResellerInfo = WhiteLabelConfig & { resellerId: string; } | null;

@Injectable({
  providedIn: 'root'
})
export class AppService {
  private auth: Auth = inject(Auth);
  private http = inject(HttpClient);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private sanitizer = inject(DomSanitizer);
  private update = inject(SwUpdate);
  private breakpoints = inject(BreakpointObserver);

  isDesktop$ = this.breakpoints.observe(Breakpoints.Handset).pipe(map(b => !b.matches));
  newVersion$ = this.update.versionUpdates.pipe(filter(e => e.type == 'VERSION_READY'));

  message$ = new Subject<MessagePayload>();

  idToken$ = idToken(this.auth);
  authState$ = authState(this.auth);
  // firebaseUser$ = user(this.auth);
  idToken?: string;

  isMainOrigin = true;
  private _reseller$ = new BehaviorSubject<ResellerInfo | undefined>(undefined);

  reseller$ = firstValueFrom(this._reseller$.asObservable().pipe(filter(reseller => reseller !== undefined)));

  constructor() {
    this.isMainOrigin = location.origin?.toLowerCase() === environment.app.mainOrigin.toLowerCase();
    this.idToken$.subscribe(async (idToken: string | null) => {
      this.idToken = idToken || undefined;
    });

    // check for updates every hour
    interval(3600000).subscribe(() => this.update.checkForUpdate());

    setTimeout(async () => await this.loadReseller(), 0);
    // this.authState$.subscribe(async (auth: any) => { console.debug('auth:authState', { auth }) });
    // this.firebaseUser$.subscribe(async (user: any) => { console.debug('auth:firebaseUser', { user }) });
  }

  get clientId() {
    let clientId = localStorage.getItem('voxr-client-id');
    if (!clientId) {
      clientId = crypto.randomUUID();
      localStorage.setItem('voxr-client-id', clientId);
    }
    return clientId;
  }
  resetClientId() {
    localStorage.removeItem('voxr-client-id');
  }


  translate(str: string) {
    if (!str) return str;
    const translations = this._reseller$.value?.ui?.translations || {} as { [key: string]: string; };
    return translateWithCasing(str, translations);
  }

  private async loadReseller() {
    if (this.isMainOrigin) {
      this._reseller$.next(null);
      return;
    }
    if (this._reseller$.value !== undefined) return;
    const reseller = await firstValueFrom(this.http.get<ResellerInfo>(`${environment.app.serviceUrl}/user/reseller-info`));
    this._reseller$.next(reseller || null);
  }

  async customTokenLogin(token: string) {
    return await signInWithCustomToken(this.auth, token);
  }

  async checkUser(credentials: UserCredential, resellerId: string) {
    const token = await credentials.user.getIdToken();
    await firstValueFrom(this.http.post(`${environment.app.serviceUrl}/user/check`, { token, resellerId }));
  }

  async googleLogin(resellerId: string) {
    const credentials = await signInWithPopup(this.auth, new GoogleAuthProvider());
    await this.checkUser(credentials, resellerId);
  }

  async getSignInMethod(email: string) {
    return await fetchSignInMethodsForEmail(this.auth, email);
  }

  async signUp(email: string, password: string, resellerId: string) {
    const credentials = await createUserWithEmailAndPassword(this.auth, email, password);
    await this.checkUser(credentials, resellerId);
    await this.sendEmailVerificationIfRequired();
  }

  getAuthUser() {
    return this.auth.currentUser;
  }
  async refreshIdToken() {
    await this.auth.currentUser?.getIdToken(true);
  }

  async sendEmailVerificationIfRequired() {
    const user = this.auth.currentUser;
    if (!user) throw new Error('No user found');
    if (user.emailVerified) return;
    await sendEmailVerification(user!, { url: location.href || `${environment.app.mainOrigin}/profile` });
    this.message$.next({ severity: 'info', summary: 'Email Verification', detail: 'Please check your email for verification link.', duration: 10000 });
  }

  async passwordLogin(email: string, password: string, resellerId: string) {
    try {
      await this.getSignInMethod(email);
      const credentials = await signInWithEmailAndPassword(this.auth, email, password);
      await this.checkUser(credentials, resellerId);
      await this.sendEmailVerificationIfRequired();
    } catch (e: any) {
      this.message$.next({ severity: 'danger', summary: 'Login Error', detail: e.message });
      throw e;
    }
  }

  async forgotPassword(email: string) {
    try {
      await sendPasswordResetEmail(this.auth, email, { url: location.href || `${environment.app.mainOrigin}/login` });
      this.message$.next({ severity: 'success', summary: 'Password Reset', detail: 'Please check your email for password reset instructions.', duration: 10000 });
    } catch (e: any) {
      this.message$.next({ severity: 'danger', summary: 'Password Reset Error', detail: e.message });
      throw e;
    }
  }

  async logout(navigate = true) {
    await signOut(this.auth);
    if (navigate) {
      if (await this.reseller$) location.href = this.getSsoLoginUrl('logout');
      else this.router.navigate(['/login'], { queryParams: this.route.snapshot.queryParams });
    }
  }

  safeCss(input: string) {
    if (!input) return;
    // Strip all tags
    input = input.replace(/<\/?[^>]+(>|$)/g, "");

    return this.sanitizer.bypassSecurityTrustHtml(`<style>${input}</style>`);
  }


  resellerById(resellerId: string) {
    return firstValueFrom(this.http.get<WhiteLabelConfig & { resellerId: string }>(`${environment.app.serviceUrl}/user/reseller-info/${resellerId}`));
  }

  getResellerByOrigin(origin: string) {
    return firstValueFrom(this.http.get<any>(`${environment.app.serviceUrl}/user/reseller-info`, { params: { origin } }));
  }
  getResellerByUserId(userId: string) {
    return firstValueFrom(this.http.get<any>(`${environment.app.serviceUrl}/user/user-reseller/${userId}`));
  }

  getSsoLoginUrl(action?: string): string {
    return `${environment.app.mainOrigin}/login?${SSO_REDIRECT_PARAM}=${encodeURIComponent(location.href)}${action ? `&action=${encodeURIComponent(action)}` : ''}`;
  }

  setBranding(brand?: ResellerInfo) {
    const icon = brand?.ui?.['logoUrl'] || '/assets/images/logo/voxr-icon.png';
    addHeaderTag('link', { rel: 'icon', type: 'image/x-icon', href: icon });
    const colorScheme = brand?.ui?.['color-scheme'] || 'dark';
    document.documentElement.setAttribute('color-scheme', colorScheme);
    addHeaderTag('meta', { name: 'color-scheme', content: colorScheme });
    const brandCss = (brand?.ui?.['design-tokens']) ? this.safeCss(`
      :root[color-scheme=${colorScheme}]{
      ${brand.ui['design-tokens']}
      }`) : undefined;

    let currentRoute = this.route;
    while (currentRoute.firstChild) currentRoute = currentRoute.firstChild;
    const title = currentRoute.snapshot.data?.['title'];
    document.title = `${title ? title + ' - ' : ''}${brand?.name || 'Voxr.AI'}`;

    return { brandCss, icon };
  }

}
